/**
 * Copyright © 2026 Adnuntius AS.
 */
import {firstValueFrom} from 'rxjs';
import {SignalResourceListBindingService} from '../../common/controller/signal-resource-list-binding.service';
import {DestroyRef, inject, Component, DoCheck, Inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, signal} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import * as _ from 'lodash';
import template from './team-analysis-page.component.html';

import {LocalNetworkInfo} from '../../../components/session/local-network-info';
import {configureLineItemList, enrichLineItemList} from '../../line-item/line-item-commons';
import {AppLocaleService} from '../../common/locale/app-locale.service';
import {OrderApiService} from '../../order/order-api.service';
import {TeamApiService} from './team-api.service';
import {LineItemApiService, LineItemModel} from '../../line-item/line-item-api.service';
import {SignalAdminResourceListState} from '../../common/controller/signal-admin-resource-list-state';
import {RoleApiService} from '../role/role-api.service';
import {UserApiService} from '../user/user-api.service';
import {AdnTimeShifterService} from '../../../components/util/adn-time-shifter.service';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../../components/session/local-user-permissions';
import {ADVERTISING_SHARED} from '../../advertising/advertising-shared';
import {LineItemListPageComponent} from '../../line-item/line-item-list-page.component';

type TeamAnalysisTab = 'members' | 'lineItems';

interface TeamLineItemListHost {
  totalCountText: string;
  listState?: SignalAdminResourceListState<LineItemModel>;
  disposeList?: () => void;
  allFields?: any[];
  perms?: any[];
}

@Component({
  standalone: true,
  imports: [...ADVERTISING_SHARED, LineItemListPageComponent],
  selector: 'adn-team-analysis-page',
  template
})
export class TeamAnalysisPageComponent implements OnInit, OnChanges, DoCheck, OnDestroy {
  private readonly listLifetime = inject(DestroyRef);
  @Input() model: any;
  @Input() isNew: boolean;

  effectiveIsNew = false;

  activeTab: TeamAnalysisTab = 'members';

  private readonly rolesState = signal<any[]>([]);
  private readonly usersState = signal<any[]>([]);
  private readonly otherUsersState = signal<any[]>([]);
  private readonly teamMemberRoleOptionsState = signal<any[]>([]);
  private readonly hasNewUserState = signal(false);
  private readonly loadedState = signal(false);
  private readonly lineItemsState = signal<TeamLineItemListHost>({totalCountText: ''});

  get roles(): any[] { return this.rolesState(); }
  set roles(value: any[]) { this.rolesState.set(value); }
  get users(): any[] { return this.usersState(); }
  set users(value: any[]) { this.usersState.set(value); }
  get otherUsers(): any[] { return this.otherUsersState(); }
  set otherUsers(value: any[]) { this.otherUsersState.set(value); }
  get teamMemberRoleOptions(): any[] { return this.teamMemberRoleOptionsState(); }
  set teamMemberRoleOptions(value: any[]) { this.teamMemberRoleOptionsState.set(value); }
  get hasNewUser(): boolean { return this.hasNewUserState(); }
  set hasNewUser(value: boolean) { this.hasNewUserState.set(value); }
  get loaded(): boolean { return this.loadedState(); }
  set loaded(value: boolean) { this.loadedState.set(value); }
  get lineItems(): TeamLineItemListHost { return this.lineItemsState(); }
  set lineItems(value: TeamLineItemListHost) { this.lineItemsState.set(value); }
  /** Seed badge text — bare {} left totalCountText undefined→count (NG0100). */
  seesLinks = false;

  private readonly networkId: string;
  private lastLoadedModelId: string | undefined;
  private destroyed = false;

  constructor(
    private readonly roleApi: RoleApiService,
    private readonly userApi: UserApiService,
    private readonly teamApi: TeamApiService,
    private readonly orderApi: OrderApiService,
    @Inject(LineItemApiService) private readonly LineItem: LineItemApiService,
    private readonly listBinding: SignalResourceListBindingService,
    @Inject(AdnTimeShifterService) private readonly adnTimeShifter: any,
    private readonly translate: TranslateService,
    private readonly appLocale: AppLocaleService,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()
  ) {
    this.networkId = LocalNetworkProfile.getNetworkId();
    this.seesLinks = this.LocalUserPermissions.hasNetworkPermission('MANAGE_SYSTEM');
  }

  ngOnInit(): void {
    this.effectiveIsNew = !!this.isNew;
    if (this.effectiveIsNew) {
      this.loaded = true;
      return;
    }
    if (this.model?.id) {
      this.lastLoadedModelId = this.model.id;
      this.refreshUsers();
      this.setupLineItems();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isNew) {
      this.effectiveIsNew = !!this.isNew;
    }
  }

  ngDoCheck(): void {
    const modelId = this.model?.id;
    if (modelId === this.lastLoadedModelId || this.effectiveIsNew) {
      return;
    }
    this.lastLoadedModelId = modelId;
    // Macrotask — starting list/user loads mid-CD lets totalCountText land in the
    // same verify pass as the first badge bind (undefined/'')→count NG0100.
    setTimeout(() => {
      if (this.destroyed || this.lastLoadedModelId !== modelId) {
        return;
      }
      this.refreshUsers();
      this.setupLineItems();
    }, 0);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
  }

  get displayLoaded(): boolean {
    return this.loaded;
  }

  trackByUserId(_index: number, user: any): string {
    return user.id;
  }

  trackByOtherUserId(_index: number, user: any): string {
    return user.id;
  }

  selectTab(tab: TeamAnalysisTab): void {
    this.activeTab = tab;
  }

  addUser(): void {
    if (!this.otherUsers.length || !this.teamMemberRoleOptions.length) {
      return;
    }
    this.hasNewUser = true;
    const defaultUser = this.otherUsers[0];
    this.users.unshift({
      id: defaultUser.id,
      username: defaultUser.username,
      displayName: defaultUser.displayName,
      objectState: defaultUser.objectState,
      obfuscated: defaultUser.obfuscated,
      newUser: true,
      active: true,
      edited: true,
      teamRole: this.teamMemberRoleOptions[0]
    });
  }

  newUserChange(user: any): void {
    const otherUser = _.find(this.otherUsers, ['id', user.id]);
    if (!otherUser) {
      return;
    }
    user.username = otherUser.username;
    user.displayName = otherUser.displayName;
    user.objectState = otherUser.objectState;
    user.obfuscated = otherUser.obfuscated;
  }

  editUser(user: any): void {
    user.edited = true;
    user.active = true;
    user.prevRole = user.teamRole;
  }

  cancelUser(user: any, index: number): void {
    user.edited = false;
    user.teamRole = user.prevRole;
    user.failSave = false;
    if (user.newUser) {
      _.pullAt(this.users, index);
      this.hasNewUser = false;
    }
  }

  saveUser(user: any): void {
    firstValueFrom(this.userApi.get(user.id)).then((apiUser: any) => {
      const networkRoles = _.find(apiUser.userRoles.rolesByNetwork, ['network.tagId', this.networkId]);
      if (networkRoles && networkRoles.roles) {
        const team = _.find(networkRoles.roles, ['team.id', this.model.id]);
        if (user.active) {
          const roleUpdate = {id: user.teamRole.id};
          if (team) {
            team.role = roleUpdate;
          } else {
            networkRoles.roles.push({team: {id: this.model.id}, role: roleUpdate});
          }
        } else {
          const indexEntryRemove = _.findIndex(networkRoles.roles, ['team.id', this.model.id]);
          _.pullAt(networkRoles.roles, indexEntryRemove);
        }
      }
      return firstValueFrom(this.userApi.savePartial(apiUser, 'userRoles'));
    }).then(() => {
      user.edited = false;
      user.failSave = false;
      if (user.newUser) {
        user.newUser = false;
        _.remove(this.otherUsers, (otherUser: any) => otherUser.id === user.id);
      } else if (!user.active) {
        _.remove(this.users, (item: any) => item.id === user.id);
        this.otherUsers.push(user);
      }
      this.users = [...this.users];
      this.otherUsers = [...this.otherUsers];
      this.hasNewUser = false;
    }).catch(() => {
      user.failSave = true;
      this.users = [...this.users];
    });
  }

  private setupLineItems(): void {
    if (!this.model?.id) {
      return;
    }
    this.lineItems?.disposeList?.();
    this.lineItems = {totalCountText: ''};
    this.lineItems = this.listBinding.create(this.LineItem, '', this.listLifetime, {
      moreParams: {team: this.model.id},
      baseTable: 'lineItem',
      afterFill: () => {
        this.lineItems.perms = [];
        if (this.lineItems.allFields) {
          enrichLineItemList(
            this.lineItems,
            this.LocalNetworkProfile,
            this.adnTimeShifter,
            this.translate,
            this.appLocale,
            this.LocalUserPermissions,
            this.localNetworkInfo
          );
        } else {
          configureLineItemList(
            this.lineItems,
            this.LocalNetworkProfile,
            this.adnTimeShifter,
            this.translate,
            this.appLocale,
            this.LocalUserPermissions,
            this.teamApi,
            this.orderApi,
            this.localNetworkInfo,
            'SLIM'
          );
        }
      }
    }, this.lineItems) as TeamLineItemListHost;
  }

  private refreshUsers(): void {
    if (!this.model?.id) {
      this.roles = [];
      this.users = [];
      this.otherUsers = [];
      this.teamMemberRoleOptions = [];
      this.hasNewUser = false;
      this.loaded = true;
      return;
    }

    this.loaded = false;
    firstValueFrom(this.roleApi.listOptions()).then((rolePage) => {
      return firstValueFrom(this.userApi.listOptions({
        concise: true,
        pageSize: this.localNetworkInfo.getInfo('userCount') + 50
      })).then((userPage) => {
        return {roles: rolePage.results, allUsers: userPage.results};
      });
    }).then(({roles, allUsers}) => {
      this.roles = roles;
      this.refreshTeamMemberRoleOptions();
      this.hasNewUser = false;
      _.forEach(allUsers, (user: any) => {
        user.obfuscated = user.username.indexOf('@') < 0 && user.username.indexOf('*') > 0;
        user.teamRole = null;
      });

      const selection = (user: any) => {
        const rolesByNetwork = _.find(user.userRoles.rolesByNetwork, (x: any) => {
          return x.network.id === this.networkId || x.network.tagId === this.networkId;
        });
        if (!rolesByNetwork) {
          return false;
        }
        const teamRole = _.find(rolesByNetwork.roles, ['team.id', this.model.id]);
        if (teamRole) {
          user.teamRole = _.find(this.roles, ['id', teamRole.role.id]);
        }
        return user.teamRole;
      };

      this.users = _.filter(allUsers, selection);
      this.otherUsers = _.reject(allUsers, selection);
      this.loaded = true;
    }).catch(() => {
      this.roles = [];
      this.users = [];
      this.otherUsers = [];
      this.teamMemberRoleOptions = [];
      this.hasNewUser = false;
      this.loaded = true;
    });
  }

  private refreshTeamMemberRoleOptions(): void {
    const politicalTypes = ['CONTROLLER', 'SPONSOR'];
    this.teamMemberRoleOptions = _.filter(this.roles, (r: any) => {
      if (r.scope !== 'TEAM') {
        return false;
      }
      if (politicalTypes.indexOf(this.model.type) > -1) {
        return r.application === 'POLITICAL';
      }
      return r.application !== 'POLITICAL';
    });
  }
}
